import {
  Alert,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  OutlinedInput,
  Typography,
} from "@mui/material";
import React, { FormEvent, useCallback, useState } from "react";
import LoginWithNameAndMobileStyles from "./LoginWithNameAndMobileStyles";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { login } from "../userAuthActions";
import { ILoginRequestParams } from "../../../models/user/userAuth/userAuth";
import { useTranslation } from "react-i18next";

interface IProps {
  isFunctionable: boolean;
}

const LoginWithNameAndMobile: React.FC<IProps> = ({ isFunctionable }) => {
  const dispatch = useAppDispatch();

  const { isLoading, calledReducerType, isError, message } = useAppSelector(
    (state) => state.common
  );

  const [loginData, setLoginData] = useState<ILoginRequestParams>({
    customerName: "",
    mobile: "",
    loginTime: "",
  });
  const [validationErrors, setValidationErrors] = useState({
    customerName: "",
    mobile: "",
  });

  const { t } = useTranslation();

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const { name, value } = event.target;
      setValidationErrors((prevState) => ({ ...prevState, [name]: "" }));
      if (name === "mobile") {
        const cleanedValue = value.replace(/\D/g, "");
        if (cleanedValue.length <= 10) {
          setLoginData((prev) => ({
            ...prev,
            [name]: cleanedValue,
          }));
        }
      } else {
        setLoginData((prev) => ({
          ...prev,
          [name]: value,
        }));
      }
    },
    []
  );

  const validate = useCallback(() => {
    let validationErrors = {
      customerName: "",
      mobile: "",
    };
    if (!loginData.customerName || loginData.customerName.trim().length <= 0) {
      validationErrors.customerName = t("Name is required");
    }
    if (!loginData.mobile || loginData.mobile.trim().length <= 0) {
      validationErrors.mobile = t("Mobile number is required");
    } else if (loginData.mobile.trim().length !== 10) {
      validationErrors.mobile = t("Invalid mobile number");
    }
    return validationErrors;
  }, [loginData]);

  const handleSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>): void => {
      e.preventDefault();
      const errors = validate();
      if (Object.values(errors).some((error) => error)) {
        setValidationErrors(errors);
        return;
      }
      const currentLoginTime = new Date().toString();
      dispatch(
        login({
          loginType: "direct",
          requestParams: { ...loginData, loginTime: currentLoginTime },
        })
      );
    },
    [loginData, validate, dispatch]
  );

  return (
    <form
      onSubmit={handleSubmit}
      style={LoginWithNameAndMobileStyles.loginWithNameAndMobileForm}
    >
      {isError && calledReducerType === "userAuth/login" ? (
        <Alert severity="error">
          {t(message ? message : "") ||
            t("Something went wrong, please try again...")}
        </Alert>
      ) : (
        <></>
      )}
      <FormControl disabled={!isFunctionable} fullWidth>
        <InputLabel>{t("Name")}</InputLabel>
        <OutlinedInput
          label={t("Name")}
          id="login_textfield-name"
          data-test-id="test-login_textfield-name"
          aria-label="name-entername"
          type={"text"}
          placeholder={t("Please enter your username")}
          name="customerName"
          onChange={handleChange}
          value={loginData.customerName}
          error={Boolean(validationErrors.customerName)}
        />
        {validationErrors.customerName ? (
          <Typography color="error">{validationErrors.customerName}</Typography>
        ) : (
          <></>
        )}
      </FormControl>
      <FormControl disabled={!isFunctionable} fullWidth>
        <InputLabel>{t("Mobile Number")}</InputLabel>
        <OutlinedInput
          label={t("Mobile Number")}
          id="login_textfield-mobilenumber"
          data-test-id="test-textfield-mobilenumber"
          aria-label="mobilenumber-entermobilenumber"
          type={"text"}
          placeholder={t("Please enter your mobile number")}
          name="mobile"
          onChange={handleChange}
          value={loginData.mobile}
          error={Boolean(validationErrors.mobile)}
        />
        {validationErrors.mobile ? (
          <Typography color="error">{validationErrors.mobile}</Typography>
        ) : (
          <></>
        )}
      </FormControl>
      <Button
        variant="contained"
        type="submit"
        id="login_button-login"
        data-test-id="test-login_button-login"
        aria-label="login-enterlogin"
        disabled={
          Boolean(
            validationErrors.customerName || Boolean(validationErrors.mobile)
          ) || !isFunctionable
        }
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        {isLoading && calledReducerType === "userAuth/login" && (
          <CircularProgress color="info" size="20px" />
        )}
        {t("Login")}
      </Button>
    </form>
  );
};

export default LoginWithNameAndMobile;
